import { useContext, useEffect, useState } from 'react';
import { getCustomisationValue } from '../../../EditablePreview/EditablePreview';
import ModuleTextInput from '../../components/ModuleTextInput';
import { changeStringFieldValue, changeValue_noType } from '../../ModuleList';
import { CampaignContext } from "../../../../../../../contexts/CampaignContext";
import { filesRoute } from "../../../../../../../api/routes";
import config from "../../../../../../../config";
import { CUSTOMISED_URL_MAP } from "../../../../../../../config/FeatureDashboardVarMap";

import ImageIcon from '../../../../WorkspaceAssets/ListIcons/image-icon.png';

import './ImageField.scss';
import { PANEL_CONTENT_TYPES, slideOutPanelControl } from '../../../../../../../containers/TimelineEditor/slideout-panel/SlideoutPanel';
import { WORKING_ON_NODE_DATA } from '../../../EditablePreview/VisualisedComponents/VisualisedNode';

const ImageField = (props) => {
    const fieldData = props.fieldData;
    const customisationVars = props.customisationVars;

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const rawData = getCustomisationValue(customisationVars, fieldData?.customFieldConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
    const [imageFileData, setImageFileData] = useState(() => {
        // const rawData = getCustomisationValue(customisationVars, fieldData?.customFieldConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
        return processImageData(rawData, campaignContext);
    });
    // Sometimes the rawData changes (eg. when we use undo / redo), so we need to watch for that and update our imageFileData accordingly
    useEffect(() => {
        setImageFileData(processImageData(rawData, campaignContext));
    }, [rawData?.srcUrl]);
    // Also watch for changes to imageFileData while the image uploader panel is open for this field
    // Calling openImageUploader will refresh the panel with the new data (ensuring the panel updates when we use our undo / redo functionality)
    useEffect(() => {
        if (slideOutPanelControl.panelState.isOpen && slideOutPanelControl.panelState.type === PANEL_CONTENT_TYPES.IMAGE_UPLOADER && slideOutPanelControl.panelState.fieldData === fieldData) {
            // If the image uploader panel is open for this field, refresh it with the new data
            // console.log('ImageField: Detected imageFileData change while image uploader panel is open, refreshing panel with new data:', imageFileData);
            // Trigger image uploader slide out panel
            clickHandler();
        }
    }, [imageFileData]);
    
    function processImageData(rawData, campaignContext) {
        if (!rawData) {
            console.log('ImageField: processImageData - no raw data');
            return null;
        }

        /** KLUDGE FIX:
         * The raw data should be already processed into an object with filename, srcUrl, originalFile, etc.
         * in various places where it's set, so we can just return it directly here.
         * 
         * This needs some tidying up, but for now this makes this work as expected.
         * 
         * - Carl Trelfa 03/Feb/2026
         */
        return rawData;
        
        console.log('ImageField: processImageData - rawData:', rawData, 'type:', typeof rawData, 'full:', JSON.stringify(rawData, null, 2));
        
        let filename = null;
        let isDefault = false;
        let type = null;
        
        if (typeof rawData === 'object' && rawData.filename) {
            filename = rawData.filename;
            isDefault = rawData.isDefault || false;
            type = rawData.type || null;
        } else if (typeof rawData === 'string') {
            filename = rawData;
        } else {
            return rawData; // Can't process this data
        }
        
        let srcUrl;
        
        if (isDefault && type) {
            // Default images are served from CloudFront
            srcUrl = `${config.cloudfrontBaseUrl}/feature-assets/wave/${type}/${filename}`;
        } else {
            // Custom uploaded images use filesRoute
            // Filename now always includes extension, so use it directly
            const campaignId = campaignContext?.campaignData?._id;
            srcUrl = campaignId 
                ? `${filesRoute}/feature-assets/campaigns/${campaignId}/${CUSTOMISED_URL_MAP.image.dir}/${filename}`
                : `${filesRoute}/feature-assets/${CUSTOMISED_URL_MAP.image.dir}/${filename}`;
        }
        
        // Return enhanced data object
        return {
            ...(typeof rawData === 'object' ? rawData : {}),
            filename: filename,
            srcUrl: srcUrl,
            originalFile: filename
        };
    }
    
    useEffect(() => {
        const rawData = getCustomisationValue(customisationVars, fieldData?.customFieldConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
        setImageFileData(processImageData(rawData, campaignContext));
    }, [customisationVars, fieldData, props.index, props.visualisationComponentKey, props.selectedFeatureNode, campaignContext]);

    const changeImageValue = (imageData, isFinalSelection = true) => {
        console.log('ImageField: Changing image value:', imageData, 'isFinalSelection:', isFinalSelection);
        console.log('ImageField: imageData type:', typeof imageData, 'full object:', JSON.stringify(imageData, null, 2));
        console.log('ImageField: fieldData:', fieldData, 'dataLink:', fieldData?.customFieldConfig?.dataLink);
        // Set working on node data for proper context
        WORKING_ON_NODE_DATA.fieldData = fieldData;
        WORKING_ON_NODE_DATA.node = props.selectedFeatureNode;
        
        // Extract the filename and build the proper object structure for database storage
        // Database expects: {filename: "name.png", isDefault: false, isStreamLevel: false}
        let valueToStore = null;
        if (imageData && imageData.filename) {
            let filename = null;
            
            // Extract filename from various possible formats
            if (typeof imageData === 'object' && imageData.filename) {
                filename = imageData.filename;
            } else if (typeof imageData === 'object' && imageData.originalFile) {
                filename = imageData.originalFile;
            } else if (typeof imageData === 'string') {
                filename = imageData;
            }
            
            // Only store if we have a non-empty filename
            if (filename && filename.trim() !== '') {
                valueToStore = {
                    filename: filename,
                    isDefault: false,
                    isStreamLevel: false
                };
            } else {
                // Empty filename - clear the field
                valueToStore = {
                    filename: '',
                    isDefault: false,
                    isStreamLevel: false
                };
            }
        } else {
            // imageData is null/undefined or has no filename, so clear just the filename
            // Don't pass null - pass an empty object so the field data structure remains
            valueToStore = {
                filename: '',
                isDefault: false,
                isStreamLevel: false
            };
        }
        
        console.log('ImageField: Storing value:', valueToStore);
        console.log('ImageField: Updating field with dataLink:', fieldData?.customFieldConfig?.dataLink, 'index:', props.index, 'visualisationComponentKey:', props.visualisationComponentKey);
        
        // Update using the proper changeStringFieldValue helper
        let valChanged = changeValue_noType(
            customisationVars,
            fieldData?.customFieldConfig,
            imageData, // valueToStore,
            props.index ?? 0,
            props.visualisationComponentKey ?? null,
            props.selectedFeatureNode,
            isFinalSelection  // Pass isFinalValue parameter
        );
        
        console.log('ImageField: Field change result:', valChanged);
        
        // Update local state with the full image data (for display purposes)
        if (valChanged) {
            setImageFileData(imageData);
        }
    };

    const clickHandler = () => {
        // Old temporary popup - commented out for now
        // props.openTemporaryImageUploadPopup(
        //     imageFileData,
        //     (groupConfig?.parent?.name ? groupConfig?.parent?.name + ' > ' : '') + (groupConfig?.name ? groupConfig?.name + ' > ' : '') + moduleConfig.name,
        //     'This is a temporary image uploader and is not final.',
        // )

        // Trigger image uploader slide out panel
        slideOutPanelControl.openImageUploader(
            imageFileData,
            (img) => changeImageValue(img, false),  // Preview callback (when file selected)
            (img) => changeImageValue(img, true),   // Confirm callback (when uploaded to server)
            'Upload Image',
            fieldData
        );
    }

    const [imageDimensions, setImageDimensions] = useState({width: 'unknown', height: 'unknown'});
    useEffect(() => {
        setImageDimensions({width: 'unknown', height: 'unknown'});
        if (imageFileData?.srcUrl) {
            const img = new Image();
            img.onload = () => {
                setImageDimensions({width: img.width, height: img.height});
            }
            img.src = imageFileData.srcUrl;
        }
    }, [imageFileData, imageFileData?.srcUrl]);

    return (
        <>
            <div className={`v2-module-field-label${!props.isEnabled ? ' disabled' : ''}`}>{fieldData?.customFieldConfig?.customFieldLabel ?? 'Image'}</div>
            <div className={`image-field-input-shell${props.isHighlightedField ? ' selected-image-field' : ''}`}>
                <ModuleTextInput
                    fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: '#FFFFFF', backgroundImage: `url(${ImageIcon})`, backgroundSize: '1.8rem', backgroundRepeat: 'no-repeat', backgroundPosition: 'center'}} />}
                    value={typeof imageFileData === 'string' ? imageFileData : imageFileData?.originalFile ?? '<No Image>'}
                    fieldId={'image-file'}
                    enabled={props.isEnabled}
                    editable={false}
                    onChange={null}
                    onClick={
                        props.isEnabled ? clickHandler : null
                    }
                />
            </div>
            {imageFileData?.srcUrl &&
                <div className={`image-preview-holder${!props.isEnabled ? ' v2-module-disabled-field' : ''}${props.isHighlightedField ? ' selected-image-field' : ''}`} onClick={props.isEnabled ? clickHandler : null}>
                    <img src={imageFileData?.srcUrl} alt={imageFileData?.originalFile} />
                    {imageDimensions.width !== 'unknown' &&
                        <div className="image-dimensions-label">{imageDimensions.width} x {imageDimensions.height}</div>
                    }
                    {imageDimensions.width === 'unknown' &&
                        <div className="image-dimensions-label">&nbsp;</div>
                    }
                </div>
            }
        </>
    );
};

export default ImageField;
